import { useState, type JSX } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { RESOURCE_CATEGORY_LABELS, type ResourceCategory } from '@dxtech/shared';
import { api, type CompanyDoc } from '../api/client.js';
import { useApp } from '../state/app.js';
import { ConfirmDialog, Spinner } from '../ui/common.js';

function fmtSize(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
}
function dDot(iso?: string): string {
  return iso && iso.length >= 10 ? iso.slice(0, 10).replace(/-/g, '.') : '';
}

// 문서함 — 회사 공용 문서 양식. 관리자(CEO/Admin)는 CRUD, 그 외 직원은 조회·다운로드.
export function Resources(): JSX.Element {
  const { me } = useApp();
  const isAdmin = me.permissions.canManageMembers;

  return (
    <div style={{ maxWidth: 880, margin: '0 auto', animation: 'dxfade .3s ease', display: 'flex', flexDirection: 'column', gap: 16 }}>
      <DocCategoryCard category="external" isAdmin={isAdmin} />
      <DocCategoryCard category="internal" isAdmin={isAdmin} />
    </div>
  );
}

function DocCategoryCard({ category, isAdmin }: { category: ResourceCategory; isAdmin: boolean }): JSX.Element {
  const { flash } = useApp();
  const qc = useQueryClient();
  const [busy, setBusy] = useState(false);
  const [drag, setDrag] = useState(false);
  const [pendingDoc, setPendingDoc] = useState<CompanyDoc | null>(null);

  const label = RESOURCE_CATEGORY_LABELS[category];
  const key = ['company-docs', category];
  const docs = useQuery({ queryKey: key, queryFn: () => api.companyDocs(category) });
  const items = docs.data?.items ?? [];

  const del = useMutation({
    mutationFn: (id: string) => api.deleteCompanyDoc(id),
    onSuccess: () => { flash('파일을 삭제했습니다'); qc.invalidateQueries({ queryKey: key }); },
    onError: (e) => flash(e instanceof Error ? e.message : '삭제 실패'),
  });

  const uploadFiles = async (files: FileList | File[]): Promise<void> => {
    const arr = Array.from(files);
    if (!arr.length) return;
    setBusy(true);
    try {
      let ok = 0;
      for (const file of arr) {
        if (file.size > 10 * 1024 * 1024) { flash(`${file.name}: 10MB 초과로 제외`); continue; }
        const b64 = await new Promise<string>((resolve, reject) => {
          const fr = new FileReader();
          fr.onload = () => resolve(String(fr.result).split(',')[1] ?? '');
          fr.onerror = () => reject(new Error('파일 읽기 실패'));
          fr.readAsDataURL(file);
        });
        await api.uploadCompanyDoc({ category, fileName: file.name, contentBase64: b64, contentType: file.type || 'application/octet-stream' });
        ok++;
      }
      if (ok) flash(`${ok}개 파일을 업로드했습니다`);
      qc.invalidateQueries({ queryKey: key });
    } catch (e) {
      flash(e instanceof Error ? e.message : '업로드 실패');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="dx-card" style={{ overflow: 'hidden' }}>
      <div style={{ padding: '16px 18px 13px', borderBottom: '1px solid #F3F4F6', display: 'flex', alignItems: 'center', gap: 10 }}>
        <div style={{ fontSize: 15, fontWeight: 700 }}>{label}</div>
        {items.length > 0 && <span style={{ fontSize: 12, color: '#9CA3AF' }}>({items.length})</span>}
        {isAdmin && (
          <label className="dx-btn dx-btn-soft" style={{ marginLeft: 'auto', padding: '6px 12px', fontSize: 12.5, cursor: busy ? 'wait' : 'pointer', opacity: busy ? 0.6 : 1 }}>
            {busy ? '업로드 중…' : '파일 선택'}
            <input type="file" hidden multiple disabled={busy}
              accept=".pdf,.jpg,.jpeg,.png,.gif,.bmp,.webp,.hwp,.hwpx,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.zip"
              onChange={(e) => { if (e.target.files?.length) void uploadFiles(e.target.files); e.target.value = ''; }} />
          </label>
        )}
      </div>
      <div style={{ padding: 18 }}>
        {isAdmin && (
          <div
            onDragOver={(e) => { e.preventDefault(); if (!busy) setDrag(true); }}
            onDragLeave={() => setDrag(false)}
            onDrop={(e) => { e.preventDefault(); setDrag(false); if (!busy && e.dataTransfer.files?.length) void uploadFiles(e.dataTransfer.files); }}
            style={{
              border: `2px dashed ${drag ? '#2563EB' : '#D1D5DB'}`, borderRadius: 12, padding: '22px 16px', textAlign: 'center',
              background: drag ? '#EFF6FF' : '#FAFBFC', color: drag ? '#2563EB' : '#9CA3AF', marginBottom: 14,
              transition: 'all .15s', fontSize: 13, fontWeight: 600,
            }}>
            {busy ? '업로드 중…' : drag ? '여기에 놓으면 업로드됩니다' : '여기로 파일을 끌어다 놓거나, 우측 상단 “파일 선택”을 누르세요'}
            <div style={{ fontSize: 11, fontWeight: 500, color: '#C0C4CB', marginTop: 4 }}>PDF·이미지·문서 등 · 1개 파일 최대 10MB · 여러 파일 동시 가능</div>
          </div>
        )}

        {docs.isLoading ? (
          <div style={{ padding: 24, textAlign: 'center' }}><Spinner size={22} /></div>
        ) : items.length === 0 ? (
          <div style={{ padding: 18, textAlign: 'center', color: '#9CA3AF', fontSize: 13 }}>등록된 {label}이(가) 없습니다.</div>
        ) : (
          <div style={{ border: '1px solid #EDEFF2', borderRadius: 10, overflow: 'hidden' }}>
            {items.map((d) => {
              const ext = (d.name.split('.').pop() ?? '').toUpperCase().slice(0, 4);
              return (
                <div key={d.id} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '11px 14px', borderBottom: '1px solid #F6F7F9', background: '#fff' }}>
                  <div style={{ width: 36, height: 36, borderRadius: 8, background: '#EEF2F6', color: '#475569', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10.5, fontWeight: 800, flexShrink: 0 }}>{ext || '파일'}</div>
                  <div style={{ minWidth: 0, flex: 1 }}>
                    <div style={{ fontWeight: 600, fontSize: 13.5, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{d.name}</div>
                    <div style={{ fontSize: 11.5, color: '#9CA3AF' }}>{dDot(d.uploadedAt)} · {fmtSize(d.size)}</div>
                  </div>
                  <button className="dx-btn dx-btn-ghost" style={{ padding: '5px 11px', fontSize: 12, flexShrink: 0 }}
                    onClick={() => { void api.downloadCompanyDoc(d.id, d.name).catch((e) => flash(e instanceof Error ? e.message : '다운로드 실패')); }}>다운로드</button>
                  {isAdmin && (
                    <button title="삭제" onClick={() => setPendingDoc(d)}
                      style={{ border: 'none', background: 'transparent', cursor: 'pointer', color: '#DC2626', fontSize: 16, lineHeight: 1, padding: '0 4px', flexShrink: 0 }}>×</button>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {pendingDoc && (
        <ConfirmDialog title="파일 삭제" message={`'${pendingDoc.name}' 파일을 삭제할까요?`} confirmLabel="삭제" danger busy={del.isPending}
          onConfirm={() => { del.mutate(pendingDoc.id); setPendingDoc(null); }}
          onCancel={() => setPendingDoc(null)} />
      )}
    </div>
  );
}
